import React, { createContext, useContext, useState, ReactNode } from "react";

// チャット情報の型
interface BrowsingContextType {
  triggerFetch: boolean;
  processing: { [chatUuid: string]: boolean };
  inputQuery: { [chatUuid: string]: string };
  refreshHistoriesFlag: boolean;
  // 追加: AI状態をチャットUUIDごとに管理
  aiStatus: { [chatUuid: string]: string | null };
  // 追加: リクエスト中のチャットUUID
  requestingChatUuid: string | null;
  triggerChatFetch: () => void;
  setProcessing: (chatUuid: string, processing: boolean) => void;
  setInputQuery: (chatUuid: string, inputQuery: string) => void;
  refreshChatHistories: () => void;
  // 追加: AI状態を設定するメソッド
  setAiStatus: (chatUuid: string, status: string | null) => void;
  // 追加: リクエスト中のチャットUUIDを設定するメソッド
  setRequestingChatUuid: (chatUuid: string | null) => void;
}

// コンテキスト作成
const BrowsingContext = createContext<BrowsingContextType | undefined>(undefined);

// Provider
export const BrowsingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // 各状態を個別に管理
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [processing, setProcessingState] = useState<{ [chatUuid: string]: boolean }>({});
  const [inputQuery, setInputQueryState] = useState<{ [chatUuid: string]: string }>({});
  const [refreshHistoriesFlag, setRefreshHistoriesFlag] = useState(false);
  // 追加: AI状態をチャットUUIDごとに管理
  const [aiStatus, setAiStatusState] = useState<{ [chatUuid: string]: string | null }>({});
  // 追加: リクエスト中のチャットUUID
  const [requestingChatUuid, setRequestingChatUuidState] = useState<string | null>(null);

  // チャット情報の更新をトリガー
  const triggerChatFetch = () => {
    setTriggerFetch((prev) => !prev);
  };

  // 処理中状態の設定
  const setProcessing = (chatUuid: string, isProcessing: boolean) => {
    setProcessingState((prev) => ({
      ...prev,
      [chatUuid]: isProcessing,
    }));
  };

  // 入力クエリの設定
  const setInputQuery = (chatUuid: string, query: string) => {
    setInputQueryState((prev) => ({
      ...prev,
      [chatUuid]: query,
    }));
  };

  // チャット履歴更新のトリガー
  const refreshChatHistories = () => {
    setRefreshHistoriesFlag((prev) => !prev);
  };

  // 追加: AI状態を設定するメソッド
  const setAiStatus = (chatUuid: string, status: string | null) => {
    setAiStatusState((prev) => ({
      ...prev,
      [chatUuid]: status,
    }));
  };

  // 追加: リクエスト中のチャットUUIDを設定するメソッド
  const setRequestingChatUuid = (chatUuid: string | null) => {
    setRequestingChatUuidState(chatUuid);
  };

  // コンテキスト値
  const contextValue: BrowsingContextType = {
    triggerFetch,
    processing,
    inputQuery,
    refreshHistoriesFlag,
    aiStatus,
    requestingChatUuid,
    triggerChatFetch,
    setProcessing,
    setInputQuery,
    refreshChatHistories,
    setAiStatus,
    setRequestingChatUuid,
  };

  return <BrowsingContext.Provider value={contextValue}>{children}</BrowsingContext.Provider>;
};

// カスタムフック
export const useBrowsingContext = () => {
  const context = useContext(BrowsingContext);
  if (context === undefined) {
    throw new Error("useBrowsingContext must be used within a BrowsingProvider");
  }
  return context;
};
